import { useEffect, useMemo, useState } from 'react'
import { parseQuery } from '@calendly/http-tools'
import { useNewCode } from '@calendly/feature-check-react'

import { consentModule } from 'services/consent/consent_module'
import { setVendorRuntimeContext } from 'services/consent/consent_state_publish'
import { onVendorRuntimeChange } from 'services/vendors/vendor_runtime'

const isConsentDebugEnabled = () => {
  try {
    const { calendly_consent_debug: debug } = parseQuery(window.location.search)
    return debug === '1' || debug === 'true'
  } catch {
    return false
  }
}

const overlayStyle = {
  position: 'fixed',
  bottom: 12,
  right: 12,
  zIndex: 99999,
  maxWidth: 360,
  maxHeight: '70vh',
  overflow: 'auto',
  padding: 12,
  borderRadius: 8,
  background: 'rgba(20, 20, 30, 0.94)',
  color: '#f5f5f5',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 11,
  lineHeight: 1.4,
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.35)',
}

const sectionTitleStyle = {
  margin: '0 0 6px',
  fontSize: 10,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#a0aec0',
}

const categoryRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8,
  padding: '2px 0',
}

const vendorCardStyle = (active) => ({
  marginBottom: 6,
  padding: '6px 8px',
  borderRadius: 4,
  border: `1px solid ${active ? '#38a169' : '#4a5568'}`,
  background: active ? 'rgba(56, 161, 105, 0.12)' : 'rgba(74, 85, 104, 0.2)',
})

/**
 * Dev/test overlay for embed consent + vendor activation. Renders only when
 * `?calendly_consent_debug=1` (or `true`) is present on the iframe URL.
 */
export const ConsentDebugOverlay = () => {
  const debugEnabled = useMemo(() => isConsentDebugEnabled(), [])
  const bookingStorageEnabled = useNewCode('growth_booking_data_storage')?.enabled
  const [consentState, setConsentState] = useState(() => consentModule.getState())
  const [vendorRenderKey, setVendorRenderKey] = useState(0)

  const vendorContext = useMemo(() => ({
    featureFlags: { growth_booking_data_storage: bookingStorageEnabled },
  }), [bookingStorageEnabled])

  useEffect(() => {
    setVendorRuntimeContext(vendorContext)
  }, [vendorContext])

  useEffect(() => {
    if (!debugEnabled) return undefined

    const sync = (state) => setConsentState(state)
    sync(consentModule.getState())
    const unsubscribeConsent = consentModule.onChange(sync)
    const unsubscribeVendor = onVendorRuntimeChange(() => setVendorRenderKey((n) => n + 1))
    return () => {
      unsubscribeConsent()
      unsubscribeVendor()
    }
  }, [debugEnabled])

  if (!debugEnabled) return null

  const vendors = consentModule.getVendorStatuses(vendorContext)

  return (
    <div style={overlayStyle} data-testid="consent-debug-overlay" data-vendor-revision={vendorRenderKey}>
      <p style={{ ...sectionTitleStyle, marginTop: 0 }}>Consent debug</p>
      <p style={{ margin: '0 0 8px', color: '#cbd5e0' }}>
        source:
        {' '}
        {consentState.source}
      </p>

      <p style={sectionTitleStyle}>Categories</p>
      {Object.entries(consentState.categories).map(([category, value]) => (
        <div key={category} style={categoryRowStyle}>
          <span>{category}</span>
          <span style={{ color: value === 'granted' ? '#68d391' : '#fc8181' }}>{value}</span>
        </div>
      ))}

      <p style={{ ...sectionTitleStyle, marginTop: 12 }}>Vendors</p>
      {vendors.map((vendor) => (
        <div key={vendor.key} style={vendorCardStyle(vendor.active)}>
          <div style={{ fontWeight: 600 }}>{vendor.displayName}</div>
          <div style={{ color: '#a0aec0' }}>
            {vendor.key}
            {' · '}
            {vendor.category}
          </div>
          <div style={{ marginTop: 4 }}>
            active:
            {' '}
            <span style={{ color: vendor.active ? '#68d391' : '#fc8181' }}>
              {String(vendor.active)}
            </span>
            {' · '}
            consent:
            {' '}
            {String(vendor.consentGranted)}
            {' · '}
            enabled:
            {' '}
            {String(vendor.enabled)}
            {' · '}
            init:
            {' '}
            {String(vendor.initialized)}
          </div>
        </div>
      ))}
    </div>
  )
}
